import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import LoginPage from "@/pages/LoginPage";
import ScanPage from "@/pages/ScanPage";
import RegistrationWizard from "@/pages/RegistrationWizard";
import FamilyDashboard from "@/pages/FamilyDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import MonitorDashboard from "@/pages/MonitorDashboard";
import PrivacidadPage from "@/pages/PrivacidadPage";
import AvisoLegalPage from "@/pages/AvisoLegalPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ScanMonitorPage from "@/pages/ScanMonitorPage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import Footer from "@/components/Footer";

const FAMILY_ROLES = ["family"];
const ADMIN_ROLES = ["admin"];
const MONITOR_ROLES = ["admin", "monitor"];

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF8F3]">
        <div className="w-8 h-8 border-3 border-[#48293A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const redirectMap = { admin: "/admin", monitor: "/monitor", family: "/family" };
    return <Navigate to={redirectMap[user.role] || "/"} replace />;
  }
  if (user.role === "family" && !user.registration_complete && !window.location.pathname.startsWith("/register")) {
    return <Navigate to="/register" replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF8F3]">
        <div className="w-8 h-8 border-3 border-[#48293A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
    <Routes>
      <Route path="/" element={
        user ? <Navigate to={
          user.role === "admin" ? "/admin" :
          user.role === "monitor" ? "/monitor" :
          user.registration_complete ? "/family" : "/register"
        } replace /> : <LoginPage />
      } />
      <Route path="/scan" element={<ScanPage />} />
      <Route path="/privacidad" element={<PrivacidadPage />} />
      <Route path="/aviso-legal" element={<AvisoLegalPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/scan-monitor" element={<ScanMonitorPage />} />
      <Route path="/actividades" element={<ActivitiesPage />} />
      <Route path="/register" element={
        <ProtectedRoute allowedRoles={FAMILY_ROLES}>
          <RegistrationWizard />
        </ProtectedRoute>
      } />
      <Route path="/family" element={
        <ProtectedRoute allowedRoles={FAMILY_ROLES}>
          <FamilyDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/monitor" element={
        <ProtectedRoute allowedRoles={MONITOR_ROLES}>
          <MonitorDashboard />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <Footer />
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
